import axios from 'axios';
import { useAuthStore } from '../store/authStore';

const AUTH_SERVER_URL = import.meta.env.VITE_AUTH_SERVER_URL;

interface AuthServerLoginResponse {
  message: string;
  user: {
    id: string;
    tenant_id: string;
    email: string;
    full_name: string;
    role: string;
  };
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

interface LoginRequest {
  email: string;
  password: string;
  tenant_slug?: string;
  app_type: 'chat';
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: {
    id: string;
    tenant_id: string;
    email: string;
    full_name: string;
    role: string;
  };
}

interface User {
  id: string;
  tenant_id: string;
  email: string;
  full_name: string;
  role: string;
}

interface ApiError extends Error {
  response?: {
    data?: {
      message?: string;
    };
  };
}

class AuthService {
  /**
   * Login with email and password using Auth Server
   */
  async login(email: string, password: string, tenant_slug?: string): Promise<{ success: boolean; data?: LoginResponse; message?: string }> {
    try {
      const payload: LoginRequest = { email, password, app_type: 'chat' };
      if (tenant_slug) {
        payload.tenant_slug = tenant_slug;
      }

      const response = await axios.post<AuthServerLoginResponse>(
        `${AUTH_SERVER_URL}/api/v1/auth/login`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      return {
        success: true,
        data: {
          accessToken: response.data.tokens.accessToken,
          refreshToken: response.data.tokens.refreshToken,
          expiresIn: 86400, // 24 hours - default from Auth Server
          user: response.data.user,
        },
      };
    } catch (error) {
      const apiError = error as ApiError;
      console.error('Login error:', apiError);
      return {
        success: false,
        message: apiError.response?.data?.message || apiError.message || 'Login failed. Please try again.',
      };
    }
  }

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refresh_token: string): Promise<{ accessToken: string } | null> {
    try {
      const response = await axios.post(
        `${AUTH_SERVER_URL}/api/v1/auth/refresh`,
        { refresh_token },
        {
          headers: {
            'Content-Type': 'application/json',
          },
          timeout: 10000,
        }
      );

      // Store new tokens using Zustand store (persist middleware handles localStorage)
      const { accessToken, refreshToken: newRefreshToken } = response.data.tokens;

      useAuthStore.getState().login({
        accessToken,
        refreshToken: newRefreshToken
      });

      return { accessToken };
    } catch (error) {
      console.error('Failed to refresh token:', error);

      // Clear auth state
      useAuthStore.getState().logout();

      // DON'T redirect here - return null and let caller handle redirect
      // This allows React Router to handle navigation properly
      return null;
    }
  }

  /**
   * Logout - blacklist JWT token on server
   */
  async logout(): Promise<void> {
    try {
      const { useAuthStore } = await import('../store/authStore');
      const token = useAuthStore.getState().token;

      if (token) {
        await axios.post(`${AUTH_SERVER_URL}/api/v1/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 5000
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with client-side logout even if server call fails
    }
  }

  /**
   * Get current user info from Auth Server
   */
  async me(token: string): Promise<User> {
    try {
      const response = await axios.get(`${AUTH_SERVER_URL}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 5000,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      throw error;
    }
  }
}

export const authService = new AuthService();
